import { FolderKanban, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { useProjects, type Project } from "@/api/queries";
import { useMe } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";

import { BudgetBar } from "./BudgetBar";
import { ProjectDialog } from "./ProjectDialogs";

type Filter = Project["status"];

export function ProjectsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const canCreate = me.permissions.manage_projects;
  const projects = useProjects();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<Filter>("active");
  const [query, setQuery] = useState("");

  const all = projects.data ?? [];
  const count = (s: Filter) => all.filter((p) => p.status === s).length;
  const q = query.trim().toLowerCase();
  const visible = all.filter((p) => p.status === filter && (!q || p.name.toLowerCase().includes(q)));
  const createButton = canCreate && (
    <Button onClick={() => setCreating(true)}>
      <Plus /> {t("projects.create")}
    </Button>
  );

  return (
    <>
      <PageHeader title={t("projects.title")} description={t("projects.subtitle")} actions={createButton} />

      {all.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Segmented
            aria-label={t("projects.filter")}
            value={filter}
            onChange={setFilter}
            options={[
              { value: "active", label: `${t("projects.filterActive")} · ${count("active")}` },
              { value: "archived", label: `${t("projects.filterArchived")} · ${count("archived")}` },
            ]}
          />
          <div className="relative w-full max-w-xs">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label={t("projects.search")}
              placeholder={t("projects.search")}
              className="h-9 pl-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {projects.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-52" />
          ))}
        </div>
      ) : projects.isError ? (
        <Card>
          <EmptyState icon={FolderKanban} title={t("common.error")} />
        </Card>
      ) : all.length === 0 ? (
        <Card>
          <EmptyState
            icon={FolderKanban}
            title={t("projects.emptyTitle")}
            description={canCreate ? t("projects.emptyDescription") : t("projects.emptyOwn")}
            action={createButton}
          />
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState
            icon={FolderKanban}
            title={
              q
                ? t("common.noResults")
                : filter === "archived"
                  ? t("projects.emptyArchived")
                  : t("projects.emptyTitle")
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}

      {creating && (
        <ProjectDialog
          onClose={() => setCreating(false)}
          onSaved={(p) => void navigate(`/projects/${p.id}`)}
        />
      )}
    </>
  );
}

function ProjectCard({ project: p }: { project: Project }) {
  const { t } = useTranslation();
  const open = p.task_counts.todo + p.task_counts.in_progress;
  const managers = p.members.filter((m) => m.is_manager);

  return (
    <Card className="relative flex flex-col gap-4 p-5 transition-shadow focus-within:ring-2 focus-within:ring-ring hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="size-3 shrink-0 rounded-full"
            style={{ backgroundColor: p.color }}
            aria-hidden="true"
          />
          <h2 className="truncate text-base font-semibold">
            {/* The link covers the whole card. */}
            <Link to={`/projects/${p.id}`} className="outline-none after:absolute after:inset-0">
              {p.name}
            </Link>
          </h2>
        </div>
        {p.status === "archived" && <Badge>{t("projects.archived")}</Badge>}
      </div>
      {p.description && <p className="-mt-2 line-clamp-2 text-muted">{p.description}</p>}

      <BudgetBar project={p} />

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span>
          <span className="text-muted">{t("projects.openTasks")}: </span>
          <span className="font-medium tabular-nums">{open}</span>
        </span>
        <span className="text-muted">{t("projects.tasksDone", { count: p.task_counts.done })}</span>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {p.members.slice(0, 5).map((m) => (
              <Tooltip key={m.user_id} content={m.full_name}>
                <span className="relative">
                  <UserAvatar name={m.full_name} className="size-7 text-[10px] ring-2 ring-surface" />
                </span>
              </Tooltip>
            ))}
          </div>
          <span className="text-xs text-muted">
            {t("projects.membersCount", { count: p.members.length })}
          </span>
        </div>
        {managers.length > 0 && (
          <span className="truncate text-xs text-muted" title={t("projects.managers")}>
            {managers.map((m) => m.full_name).join(", ")}
          </span>
        )}
      </div>
    </Card>
  );
}
